import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, ShoppingBag, Truck, CheckCircle2, MapPin, Phone, 
  User, Mail, ArrowRight, ArrowLeft, Download, FileText, 
  ShieldCheck, Globe2, Plane, Package, ShieldAlert, 
  MessageCircle, Copy, Check, Sparkles, Navigation, Layers
} from 'lucide-react';
import { useCart, useCurrency, useLanguage, useSiteSettings } from '../../contexts/SiteContexts';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, addDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { generateInvoice } from '../../lib/invoiceGenerator';
import { toast } from 'react-hot-toast';

export const CheckoutModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { cart, clearCart } = useCart();
  const { formatPrice, formatAllPrices, rates } = useCurrency();
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const { settings } = useSiteSettings();

  // Wizard Steps: 1 = Review & Multi-Currency, 2 = Customer Info & Logistics Route, 3 = Order Confirmed
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [placedOrder, setPlacedOrder] = useState<any>(null);
  const [copiedId, setCopiedId] = useState(false);

  // Form states
  const [customerName, setCustomerName] = useState(profile?.name || user?.displayName || '');
  const [customerEmail, setCustomerEmail] = useState(user?.email || '');
  const [customerPhone, setCustomerPhone] = useState(profile?.phone || '');
  const [customerAddress, setCustomerAddress] = useState(profile?.address || '');

  // Shipping & Route state
  const [destinationRegion, setDestinationRegion] = useState<'PK' | 'EU' | 'US' | 'CN' | 'GLOBAL'>('PK');
  const [shippingPayer, setShippingPayer] = useState<'hamim_cargo' | 'self_pay'>('hamim_cargo');
  const [hasInsurance, setHasInsurance] = useState<boolean>(true); // 5% insurance claim toggle

  // Payment states
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'cod'>('online');
  const [onlineMethod, setOnlineMethod] = useState<'easypaisa' | 'bank' | 'paypal'>('easypaisa');
  const [paymentScreenshot, setPaymentScreenshot] = useState<string | null>(null);
  const [pakistanCityArea, setPakistanCityArea] = useState<'other' | 'nearby' | 'sialkot'>('other');
  
  // Buyer Type (for Tax rules)
  const [buyerType, setBuyerType] = useState<'hospital' | 'doctor'>('hospital');
  const [doctorOptsInTax, setDoctorOptsInTax] = useState<boolean>(false);

  // Subtotal in USD
  const subtotal = useMemo(() => {
    return cart.reduce((acc, item) => acc + (item.discountPrice || item.price) * item.quantity, 0);
  }, [cart]);

  // Estimated weight calculation: ~0.35kg per instrument package, minimum 1.0kg
  const totalItemsCount = useMemo(() => cart.reduce((acc, i) => acc + i.quantity, 0), [cart]);
  const estimatedWeightKg = useMemo(() => {
    const calculated = totalItemsCount * 0.35;
    return Math.max(1.0, Math.round(calculated * 10) / 10);
  }, [totalItemsCount]);

  // Shipping cost calculation based on destination & weight
  const calculatedShippingCost = useMemo(() => {
    if (shippingPayer === 'self_pay') {
      return 0; // Customer pays carrier directly upon delivery
    }

    if (destinationRegion === 'PK') {
      // Local Pakistan logistics
      if (paymentMethod === 'online') {
        return 0; // Free logistics for online payment in Pakistan
      }
      if (paymentMethod === 'cod') {
        if (pakistanCityArea === 'sialkot') return 2;
        if (pakistanCityArea === 'nearby') return 6;
        return 10;
      }
      return 10;
    } else {
      // International logistics (Europe, USA, China, Global)
      if (estimatedWeightKg <= 5.0) return 100;
      if (estimatedWeightKg <= 10.0) return 150;
      return 200;
    }
  }, [destinationRegion, estimatedWeightKg, shippingPayer, paymentMethod, pakistanCityArea]);

  // Insurance cost: 5% of subtotal if opted-in
  const insuranceCost = useMemo(() => {
    if (!hasInsurance) return 0;
    return Math.round((subtotal * 0.05) * 100) / 100;
  }, [hasInsurance, subtotal]);

  // COD Surcharge logic (Only for International)
  const codSurchargeAmount = useMemo(() => {
    if (paymentMethod !== 'cod') return 0;
    if (destinationRegion === 'PK') return 0; // Handled within shipping fee directly for PK
    return Math.round((subtotal * 0.05) * 100) / 100;
  }, [paymentMethod, destinationRegion, subtotal]);

  // Sales Tax Logic (18%)
  const salesTaxAmount = useMemo(() => {
    if (buyerType === 'hospital') {
      return Math.round((subtotal * 0.18) * 100) / 100; // Mandatory for hospitals
    }
    if (buyerType === 'doctor' && doctorOptsInTax) {
      return Math.round((subtotal * 0.18) * 100) / 100; // Optional for doctors
    }
    return 0;
  }, [buyerType, doctorOptsInTax, subtotal]);

  // Grand Total in USD
  const totalAmount = useMemo(() => {
    return subtotal + calculatedShippingCost + insuranceCost + codSurchargeAmount + salesTaxAmount;
  }, [subtotal, calculatedShippingCost, insuranceCost, codSurchargeAmount, salesTaxAmount]);

  // Multi-currency values
  const subtotalAll = formatAllPrices(subtotal);
  const insuranceAll = formatAllPrices(insuranceCost);
  const shippingAll = formatAllPrices(calculatedShippingCost);
  const codSurchargeAll = formatAllPrices(codSurchargeAmount);
  const salesTaxAll = formatAllPrices(salesTaxAmount);
  const totalAll = formatAllPrices(totalAmount);

  const handleProceedToStep2 = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    setStep(2);
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!customerName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!customerEmail.trim() || !customerEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!customerPhone.trim()) {
      toast.error('Please enter your phone / WhatsApp number');
      return;
    }
    if (!customerAddress.trim()) {
      toast.error('Please enter your delivery / hospital address');
      return;
    }
    if (paymentMethod === 'online' && !paymentScreenshot) {
      toast.error('Please upload your payment screenshot (EasyPaisa/Bank/PayPal) to proceed.');
      return;
    }

    setLoading(true);
    try {
      const generatedOrderId = `HE-ORD-${Math.floor(10000 + Math.random() * 90000)}`;
      const nowIso = new Date().toISOString();

      const customerInfo = {
        name: customerName.trim(),
        email: customerEmail.trim(),
        phone: customerPhone.trim(),
        address: customerAddress.trim(),
        userType: user ? 'registered' : 'guest',
        userId: user?.uid || 'guest'
      };

      const orderData = {
        orderId: generatedOrderId,
        customerId: user?.uid || `guest-${customerPhone.replace(/[^0-9]/g, '') || Date.now()}`,
        customerName: customerInfo.name,
        customerEmail: customerInfo.email,
        customerPhone: customerInfo.phone,
        customerAddress: customerInfo.address,
        userType: customerInfo.userType,
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          code: item.code || item.id?.slice(0, 8),
          category: item.category || 'General Surgery',
          price: item.discountPrice || item.price,
          quantity: item.quantity,
          imageUrl: item.imageUrl || '',
          material: item.material || 'German AISI 410 Stainless Steel',
          quality: item.quality || 'OR Surgical Grade',
          size: item.size || 'Standard Adult Dimension'
        })),
        subtotal: subtotal,
        insuranceCost: insuranceCost,
        hasInsurance: hasInsurance,
        salesTax: salesTaxAmount,
        buyerType: buyerType,
        shippingCost: calculatedShippingCost,
        shippingMethod: shippingPayer === 'self_pay' ? 'Self-Arranged / Freight Collect' : 'Hamim DDP Air Express Cargo',
        destinationRegion: destinationRegion,
        weightKg: estimatedWeightKg,
        total: totalAmount,
        multiCurrency: {
          usd: totalAll.usd,
          pkr: totalAll.pkr,
          cny: totalAll.cny,
          eur: totalAll.eur
        },
        status: 'pending',
        paymentMethod: paymentMethod === 'online' ? `online_${onlineMethod}` : 'cash_on_delivery',
        paymentScreenshot: paymentScreenshot || null,
        codSurcharge: codSurchargeAmount,
        createdAt: nowIso,
        updatedAt: nowIso
      };

      // Save order to Firestore
      const docRef = await addDoc(collection(db, 'orders'), orderData);
      
      // Update customer order record if available
      try {
        const customerRef = doc(db, 'customers', orderData.customerId);
        const customerSnap = await getDoc(customerRef);
        if (customerSnap.exists()) {
          const prevData = customerSnap.data();
          const prevOrders = prevData.orders || [];
          await setDoc(customerRef, {
            ...prevData,
            orders: [orderData, ...prevOrders],
            totalSpent: (prevData.totalSpent || 0) + totalAmount,
            updatedAt: nowIso
          }, { merge: true });
        } else {
          await setDoc(customerRef, {
            id: orderData.customerId,
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone,
            address: customerInfo.address,
            orders: [orderData],
            totalSpent: totalAmount,
            clientCode: `HC-${Math.floor(100 + Math.random() * 900)}`,
            createdAt: nowIso,
            updatedAt: nowIso
          });
        }
      } catch (custErr) {
        console.warn("Customer record sync note:", custErr);
      }

      setPlacedOrder(orderData);
      toast.success(`Order #${generatedOrderId} Placed Successfully!`);
      clearCart();
      setStep(3);
    } catch (error: any) {
      console.error("Order placement error:", error);
      toast.error('Failed to submit order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = (format: 'pdf' | 'png', type: 'Invoice' | 'Quotation' = 'Invoice') => {
    if (!placedOrder) return;
    try {
      generateInvoice({
        orderId: placedOrder.orderId,
        customerName: placedOrder.customerName || customerName,
        phone: placedOrder.customerPhone || customerPhone,
        address: placedOrder.customerAddress || customerAddress,
        date: new Date().toLocaleDateString('en-GB') + ', ' + new Date().toLocaleTimeString(),
        items: placedOrder.items.map((it: any) => ({
          name: it.name,
          code: it.code || it.id?.slice(0, 8),
          price: it.price,
          quantity: it.quantity || 1,
          size: it.size || 'Standard Adult Dimension',
          material: it.material || 'German AISI 410 Stainless Steel',
          quality: it.quality || 'OR Surgical Premium Grade',
          usageTime: '350 Autoclave Cycles',
          usageLimit: 300,
          measurement: 'Precision Calibration',
          brand: 'Hamim Enterprises',
          origin: 'Made in Sialkot, Pakistan'
        })),
        subtotal: placedOrder.subtotal,
        shippingCost: placedOrder.shippingCost,
        insuranceCost: placedOrder.insuranceCost,
        salesTax: placedOrder.salesTax || 0,
        total: placedOrder.total,
        pkrRate: rates.PKR || 280,
        type: type
      }, format);
      toast.success(`${type} (${format.toUpperCase()}) downloaded!`);
    } catch (err) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const handleCopyOrderId = () => {
    if (!placedOrder?.orderId) return;
    navigator.clipboard.writeText(placedOrder.orderId);
    setCopiedId(true);
    toast.success('Order ID copied to clipboard!');
    setTimeout(() => setCopiedId(false), 2500);
  };

  const handleWhatsAppDispatch = () => {
    if (!placedOrder) return;
    const msg = `*NEW SURGICAL ORDER DISPATCH*%0A%0A*Order Reference:* ${placedOrder.orderId}%0A*Surgeon/Client:* ${placedOrder.customerName}%0A*Phone:* ${placedOrder.customerPhone}%0A*Destination:* ${placedOrder.customerAddress}%0A%0A*Items:*%0A${placedOrder.items.map((it: any) => `- ${it.quantity}x ${it.name} (${it.code || 'SKU'})`).join('%0A')}%0A%0A*Estimated Weight:* ${placedOrder.weightKg} KG%0A*Shipping Method:* ${placedOrder.shippingMethod}%0A*Insurance Claim:* ${placedOrder.hasInsurance ? 'Active (5% Covered)' : 'Declined'}%0A%0A*Grand Total:*%0A- USD: ${placedOrder.multiCurrency.usd}%0A- PKR: ${placedOrder.multiCurrency.pkr}%0A- CNY: ${placedOrder.multiCurrency.cny}%0A- EUR: ${placedOrder.multiCurrency.eur}%0A%0APlease confirm manufacturing and export dispatch.`;
    window.open(`https://wa.me/923235857576?text=${msg}`, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[350] flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-md"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-3xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden my-auto max-h-[92vh] flex flex-col z-10"
      >
        {/* Modal Top Header */}
        <div className="p-6 sm:p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-600 text-white rounded-2xl shadow-md shadow-indigo-100">
              <ShoppingBag size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 uppercase tracking-tight">
                  {step === 1 ? 'Order Review & Multi-Currency' : step === 2 ? 'Customer Coordinates & Route Map' : 'Order Confirmed'}
                </h2>
              </div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Hamim Enterprises • Global Surgical Export Portal
              </p>
            </div>
          </div>

          <button 
            onClick={onClose} 
            className="p-2.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-2xl transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Wizard Steps Body */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar">
          
          {/* STEP 1: CART REVIEW & 4-CURRENCY BREAKDOWN */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Logged in User notification banner */}
              {user ? (
                <div className="p-4 bg-indigo-50/80 border border-indigo-100 rounded-2xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-black">
                      <User size={18} />
                    </div>
                    <div>
                      <p className="text-xs font-black text-indigo-950 uppercase tracking-tight">
                        Logged in as: {profile?.name || user.displayName || 'Registered Surgeon'}
                      </p>
                      <p className="text-[10px] font-bold text-indigo-600 font-mono">{user.email}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-white text-indigo-700 text-[9px] font-black uppercase tracking-wider rounded-xl border border-indigo-200 shadow-sm">
                    Verified Account
                  </span>
                </div>
              ) : (
                <div className="p-4 bg-amber-50/80 border border-amber-100 rounded-2xl flex items-center gap-3">
                  <Globe2 size={20} className="text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-black text-amber-950 uppercase tracking-tight">Guest Checkout Portal</p>
                    <p className="text-[10px] font-semibold text-amber-700">
                      No account required. You will provide your hospital name, email, phone, and delivery address in the next step.
                    </p>
                  </div>
                </div>
              )}

              {/* Instruments List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs font-black uppercase tracking-widest text-slate-400">
                  <span>Selected Instruments ({cart.length})</span>
                  <span>Estimated Total Weight: {estimatedWeightKg} KG</span>
                </div>

                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {cart.map((item) => {
                    const lineTotal = (item.discountPrice || item.price) * item.quantity;
                    const p = formatAllPrices(lineTotal);
                    return (
                      <div key={item.id} className="flex items-center gap-4 p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                        <img 
                          src={item.imageUrl || 'https://images.unsplash.com/photo-1584362917165-526a968579e8?auto=format&fit=crop&q=80&w=200'} 
                          alt="" 
                          className="w-14 h-14 object-contain bg-white rounded-xl p-1 border border-slate-100 flex-shrink-0" 
                          referrerPolicy="no-referrer"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-black text-slate-900 uppercase tracking-tight text-xs truncate">{item.name}</h4>
                          <p className="text-[10px] text-slate-400 font-mono">
                            Qty: <strong className="text-slate-700">{item.quantity}</strong> • SKU: {item.code || item.id?.slice(0, 8)}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-black text-slate-900 text-xs">{p.usd}</p>
                          <p className="text-[10px] font-bold text-indigo-600">{p.pkr}</p>
                          <p className="text-[9px] text-slate-400">{p.cny} • {p.eur}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 4-Currency Subtotal Live Display */}
              <div className="p-5 bg-gradient-to-br from-slate-900 to-indigo-950 text-white rounded-3xl space-y-4 shadow-xl">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-xs font-black uppercase tracking-widest text-indigo-200 flex items-center gap-2">
                    <Globe2 size={16} /> Multi-Currency Value (All 4 Currencies)
                  </span>
                  <span className="text-[10px] font-bold bg-white/10 px-2.5 py-1 rounded-full text-indigo-100">
                    Live Conversion
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-[9px] font-black text-indigo-300 uppercase">US Dollar (USD)</p>
                    <p className="text-lg font-black text-white">{subtotalAll.usd}</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-[9px] font-black text-emerald-300 uppercase">Pakistani Rupee (PKR)</p>
                    <p className="text-lg font-black text-emerald-400">{subtotalAll.pkr}</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-[9px] font-black text-amber-300 uppercase">Chinese Yuan (CNY)</p>
                    <p className="text-base font-black text-amber-300">{subtotalAll.cny}</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-[9px] font-black text-purple-300 uppercase">Euro (EUR)</p>
                    <p className="text-base font-black text-purple-300">{subtotalAll.eur}</p>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleProceedToStep2}
                className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-950 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 active:scale-[0.99]"
              >
                Proceed to Shipping, Insurance & Route Map
                <ArrowRight size={18} />
              </button>
            </div>
          )}

          {/* STEP 2: CUSTOMER COORDINATES & ROUTE / INSURANCE CONFIGURATION */}
          {step === 2 && (
            <form onSubmit={handlePlaceOrder} className="space-y-6">
              
              {/* Back to Step 1 Button */}
              <button 
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft size={14} /> Back to Products Review
              </button>

              {/* 1. Customer Information Section */}
              <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-indigo-600" />
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">
                      {user ? '1. Verified Surgeon Coordinates' : '1. Buyer & Hospital Information (Mandatory)'}
                    </h3>
                  </div>
                  {user && (
                    <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      Auto-Populated
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                    Buyer Entity Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-2 ${buyerType === 'hospital' ? 'border-indigo-600 bg-indigo-50/50' : 'border-slate-200 bg-white hover:border-indigo-300'}`}>
                      <input type="radio" name="buyerType" value="hospital" checked={buyerType === 'hospital'} onChange={() => setBuyerType('hospital')} className="hidden" />
                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${buyerType === 'hospital' ? 'border-indigo-600' : 'border-slate-300'}`}>
                        {buyerType === 'hospital' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] font-black uppercase tracking-widest text-slate-900">Hospital / Corporate</span>
                        <span className="text-[9px] text-slate-500 font-medium">Mandatory 18% Sales Tax (GST) applies</span>
                      </div>
                    </label>

                    <label className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-2 ${buyerType === 'doctor' ? 'border-indigo-600 bg-indigo-50/50' : 'border-slate-200 bg-white hover:border-indigo-300'}`}>
                      <input type="radio" name="buyerType" value="doctor" checked={buyerType === 'doctor'} onChange={() => setBuyerType('doctor')} className="hidden" />
                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${buyerType === 'doctor' ? 'border-indigo-600' : 'border-slate-300'}`}>
                        {buyerType === 'doctor' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] font-black uppercase tracking-widest text-slate-900">Individual Doctor</span>
                        <span className="text-[9px] text-slate-500 font-medium">Sales Tax is optional / adjustable</span>
                      </div>
                    </label>
                  </div>
                </div>

                {buyerType === 'doctor' && (
                  <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={doctorOptsInTax} 
                        onChange={(e) => setDoctorOptsInTax(e.target.checked)} 
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-600"
                      />
                      <span className="text-xs font-bold text-amber-900">Include 18% Sales Tax (GST) in my Invoice for claim/records</span>
                    </label>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                      <User size={12} /> Full Name / Surgeon Title <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      required 
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Dr. / Hospital Procurement Officer"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition-all font-bold text-xs"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                      <Mail size={12} /> Email Address <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="email" 
                      required 
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="surgeon@hospital.org"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition-all font-bold text-xs"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                      <Phone size={12} /> WhatsApp / Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="tel" 
                      required 
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="+92 300 1234567 / +1 (555) 000-0000"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition-all font-bold text-xs"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                      <MapPin size={12} /> Hospital / Delivery Address <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      required 
                      value={customerAddress}
                      onChange={(e) => setCustomerAddress(e.target.value)}
                      placeholder="Surgical Dept, Suite 402, City, Country"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition-all font-bold text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* 2. Route Map & Shipping Logistics Tier */}
              <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                  <div className="flex items-center gap-2">
                    <Truck size={16} className="text-indigo-600" />
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">
                      2. Logistics Destination & Route Pricing
                    </h3>
                  </div>
                  <span className="text-[10px] font-bold text-slate-500">
                    Weight: <strong>{estimatedWeightKg} KG</strong>
                  </span>
                </div>

                {/* Destination Selector */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setDestinationRegion('PK')}
                    className={`p-3 rounded-2xl border text-left transition-all ${
                      destinationRegion === 'PK'
                        ? 'border-indigo-600 bg-indigo-50/70 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <p className="text-xs font-black text-slate-900">🇵🇰 Pakistan (Local)</p>
                    <p className="text-[10px] font-bold text-indigo-600 mt-1">
                      {estimatedWeightKg <= 5 ? '$10 (Rs. 2,800)' : estimatedWeightKg <= 10 ? '$15 (Rs. 4,200)' : '$25 (Rs. 7,000)'}
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setDestinationRegion('EU')}
                    className={`p-3 rounded-2xl border text-left transition-all ${
                      destinationRegion === 'EU'
                        ? 'border-indigo-600 bg-indigo-50/70 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <p className="text-xs font-black text-slate-900">🇪🇺 Europe (DDP Air)</p>
                    <p className="text-[10px] font-bold text-indigo-600 mt-1">
                      {estimatedWeightKg <= 5 ? '$100 (€92)' : estimatedWeightKg <= 10 ? '$150 (€138)' : '$200 (€184)'}
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setDestinationRegion('US')}
                    className={`p-3 rounded-2xl border text-left transition-all ${
                      destinationRegion === 'US'
                        ? 'border-indigo-600 bg-indigo-50/70 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <p className="text-xs font-black text-slate-900">🇺🇸 USA & Canada</p>
                    <p className="text-[10px] font-bold text-indigo-600 mt-1">
                      {estimatedWeightKg <= 5 ? '$100' : estimatedWeightKg <= 10 ? '$150' : '$200'}
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setDestinationRegion('CN')}
                    className={`p-3 rounded-2xl border text-left transition-all ${
                      destinationRegion === 'CN'
                        ? 'border-indigo-600 bg-indigo-50/70 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <p className="text-xs font-black text-slate-900">🇨🇳 China & East Asia</p>
                    <p className="text-[10px] font-bold text-indigo-600 mt-1">
                      {estimatedWeightKg <= 5 ? '$100 (¥720)' : estimatedWeightKg <= 10 ? '$150 (¥1,080)' : '$200 (¥1,440)'}
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setDestinationRegion('GLOBAL')}
                    className={`p-3 rounded-2xl border text-left transition-all col-span-2 sm:col-span-2 ${
                      destinationRegion === 'GLOBAL'
                        ? 'border-indigo-600 bg-indigo-50/70 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <p className="text-xs font-black text-slate-900">🌍 Middle East & Global Rest of World</p>
                    <p className="text-[10px] font-bold text-indigo-600 mt-1">
                      {estimatedWeightKg <= 5 ? '$100' : estimatedWeightKg <= 10 ? '$150' : '$200'} Express Cargo
                    </p>
                  </button>
                </div>

                {/* Shipping Payment Option: Website pre-pay vs Self-pay */}
                <div className="space-y-2 pt-2">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Shipping Payment Preference
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label 
                      onClick={() => setShippingPayer('hamim_cargo')}
                      className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer transition-all ${
                        shippingPayer === 'hamim_cargo'
                          ? 'border-indigo-600 bg-white ring-2 ring-indigo-100 shadow-sm'
                          : 'border-slate-200 bg-white/60 hover:bg-white'
                      }`}
                    >
                      <input 
                        type="radio" 
                        name="shipping_payer" 
                        checked={shippingPayer === 'hamim_cargo'}
                        onChange={() => setShippingPayer('hamim_cargo')}
                        className="mt-1 text-indigo-600 focus:ring-indigo-500" 
                      />
                      <div>
                        <p className="text-xs font-black text-slate-900">
                          Pre-Pay via Hamim Cargo
                        </p>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          Include +{destinationRegion === 'PK' ? (estimatedWeightKg <= 5 ? '$10 (Rs. 2,800)' : '$15 (Rs. 4,200)') : (estimatedWeightKg <= 5 ? '$100' : '$150')} directly in the invoice total.
                        </p>
                      </div>
                    </label>

                    <label 
                      onClick={() => setShippingPayer('self_pay')}
                      className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer transition-all ${
                        shippingPayer === 'self_pay'
                          ? 'border-indigo-600 bg-white ring-2 ring-indigo-100 shadow-sm'
                          : 'border-slate-200 bg-white/60 hover:bg-white'
                      }`}
                    >
                      <input 
                        type="radio" 
                        name="shipping_payer" 
                        checked={shippingPayer === 'self_pay'}
                        onChange={() => setShippingPayer('self_pay')}
                        className="mt-1 text-indigo-600 focus:ring-indigo-500" 
                      />
                      <div>
                        <p className="text-xs font-black text-slate-900">
                          Self-Pay / Freight Collect ($0 at checkout)
                        </p>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          Pay your local courier or use your own DHL/FedEx account directly upon arrival.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Route Visual Representation */}
                <div className="p-3.5 bg-indigo-950 text-white rounded-2xl space-y-2">
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-indigo-300">
                    <span className="flex items-center gap-1.5"><Navigation size={12} /> Air Logistics Route Map</span>
                    <span>ETA: {destinationRegion === 'PK' ? '2-3 Working Days' : '5-7 Days Air Cargo'}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-[11px] font-mono pt-1">
                    <div className="text-center">
                      <p className="text-indigo-400 text-[9px] font-bold">ORIGIN</p>
                      <p className="font-black text-white">Sialkot Hub, PK</p>
                    </div>
                    <div className="flex-1 mx-3 border-t-2 border-dashed border-indigo-400/40 relative">
                      <Plane size={14} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-300" />
                    </div>
                    <div className="text-center">
                      <p className="text-indigo-400 text-[9px] font-bold">TRANSIT</p>
                      <p className="font-black text-white">{destinationRegion === 'PK' ? 'Islamabad/LHE' : 'Dubai/Frankfurt'}</p>
                    </div>
                    <div className="flex-1 mx-3 border-t-2 border-dashed border-indigo-400/40 relative">
                      <Truck size={14} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-300" />
                    </div>
                    <div className="text-center">
                      <p className="text-emerald-400 text-[9px] font-bold">DELIVERY</p>
                      <p className="font-black text-emerald-300">{destinationRegion === 'PK' ? 'Local Clinic' : 'Hospital Suite'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. 5% Transit & Loss Insurance Claim Toggle */}
              <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={16} className="text-indigo-600" />
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">
                      3. Certified Surgical Transit Insurance (5%)
                    </h3>
                  </div>
                  <span className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                    Optional Protection
                  </span>
                </div>

                <label 
                  className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${
                    hasInsurance 
                      ? 'bg-white border-indigo-600 ring-2 ring-indigo-100 shadow-sm' 
                      : 'bg-white/60 border-slate-200 hover:bg-white'
                  }`}
                >
                  <input 
                    type="checkbox"
                    checked={hasInsurance}
                    onChange={(e) => setHasInsurance(e.target.checked)}
                    className="w-5 h-5 rounded-lg text-indigo-600 focus:ring-indigo-500 mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-black text-slate-900">
                        Add 5% Precision Transit & Loss Coverage (+{insuranceAll.usd} / {insuranceAll.pkr})
                      </p>
                      <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                        hasInsurance ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {hasInsurance ? 'Active (Covered)' : 'Declined'}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                      100% full replacement guarantee against accidental damage, sterile packaging seal breach, or transit loss during international air cargo transit.
                    </p>
                  </div>
                </label>
              </div>

              {/* 4. Complete Transparent Price Breakdown in All Currencies */}
              <div className="p-5 bg-white rounded-3xl border-2 border-slate-900 space-y-4 shadow-xl">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <span className="text-xs font-black uppercase tracking-widest text-slate-900 flex items-center gap-2">
                    <Layers size={15} className="text-indigo-600" /> Final Order Price Summary
                  </span>
                  <span className="text-[10px] font-bold text-slate-500 font-mono">
                    All 4 Currencies
                  </span>
                </div>

                <div className="space-y-2 text-xs font-bold">
                  <div className="flex justify-between text-slate-500">
                    <span>Instruments Subtotal ({cart.length} items, {estimatedWeightKg} KG)</span>
                    <span className="text-slate-900">{subtotalAll.usd}</span>
                  </div>

                  <div className="flex justify-between text-slate-500">
                    <span>
                      Air Cargo Route Logistics ({shippingPayer === 'self_pay' ? 'Self-Pay / Freight Collect' : `${destinationRegion} Express`})
                    </span>
                    <span className={calculatedShippingCost > 0 ? "text-slate-900" : "text-emerald-600"}>
                      {calculatedShippingCost > 0 ? shippingAll.usd : 'Free at Checkout ($0)'}
                    </span>
                  </div>

                  <div className="flex justify-between text-slate-500">
                    <span>Transit Insurance (5% {hasInsurance ? 'Included' : 'Not Selected'})</span>
                    <span className={hasInsurance ? "text-slate-900" : "text-slate-400"}>
                      {hasInsurance ? insuranceAll.usd : '$0.00'}
                    </span>
                  </div>

                  {salesTaxAmount > 0 && (
                    <div className="flex justify-between text-slate-700 font-bold border-t border-slate-100 pt-2 mt-2">
                      <span>Sales Tax (18% GST)</span>
                      <span>{salesTaxAll.usd}</span>
                    </div>
                  )}

                  {paymentMethod === 'cod' && destinationRegion !== 'PK' && (
                    <div className="flex justify-between text-orange-600 font-bold">
                      <span>International COD Surcharge (5%)</span>
                      <span>{codSurchargeAll.usd}</span>
                    </div>
                  )}

                  {/* Grand Total Row */}
                  <div className="pt-3 border-t border-slate-200 mt-2">
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm font-black uppercase tracking-widest text-slate-900">Grand Total Payable</span>
                      <span className="text-2xl font-black text-indigo-600">{totalAll.usd}</span>
                    </div>

                    {/* All Currencies Grid */}
                    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-center">
                      <div className="p-2 bg-indigo-50/60 rounded-xl">
                        <p className="text-[9px] font-black text-indigo-600 uppercase">PKR Rupee</p>
                        <p className="text-xs font-black text-indigo-950">{totalAll.pkr}</p>
                      </div>
                      <div className="p-2 bg-amber-50/60 rounded-xl">
                        <p className="text-[9px] font-black text-amber-600 uppercase">Chinese Yuan</p>
                        <p className="text-xs font-black text-amber-950">{totalAll.cny}</p>
                      </div>
                      <div className="p-2 bg-purple-50/60 rounded-xl">
                        <p className="text-[9px] font-black text-purple-600 uppercase">Euro Zone</p>
                        <p className="text-xs font-black text-purple-950">{totalAll.eur}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Payment Method & Schedule */}
              <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={16} className="text-indigo-600" />
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">
                      3. Payment Method & Schedule
                    </h3>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${paymentMethod === 'online' ? 'border-indigo-600 bg-indigo-50/30' : 'border-slate-200 bg-white hover:border-indigo-300'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-widest text-slate-900">Online Payment</span>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'online' ? 'border-indigo-600' : 'border-slate-300'}`}>
                        {paymentMethod === 'online' && <div className="w-2 h-2 rounded-full bg-indigo-600" />}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">EasyPaisa, Bank Transfer, PayPal. Requires Screenshot.</p>
                    <input type="radio" name="paymentMethod" value="online" checked={paymentMethod === 'online'} onChange={() => setPaymentMethod('online')} className="hidden" />
                  </label>

                  <label className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${paymentMethod === 'cod' ? 'border-indigo-600 bg-indigo-50/30' : 'border-slate-200 bg-white hover:border-indigo-300'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-widest text-slate-900">Cash on Delivery</span>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'cod' ? 'border-indigo-600' : 'border-slate-300'}`}>
                        {paymentMethod === 'cod' && <div className="w-2 h-2 rounded-full bg-indigo-600" />}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">Pay upon delivery. Extra logistics surcharge applies.</p>
                    <input type="radio" name="paymentMethod" value="cod" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} className="hidden" />
                  </label>
                </div>

                {paymentMethod === 'online' && (
                  <div className="p-4 bg-white rounded-2xl border border-slate-200 space-y-4 animate-fadeIn">
                    <div className="flex gap-2">
                      {['easypaisa', 'bank', 'paypal'].map(method => (
                        <button
                          key={method}
                          type="button"
                          onClick={() => setOnlineMethod(method as any)}
                          className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${onlineMethod === method ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        >
                          {method}
                        </button>
                      ))}
                    </div>

                    <div className="p-3 bg-slate-50 rounded-xl text-xs text-slate-600 font-medium space-y-1">
                      {onlineMethod === 'easypaisa' && <p>Please send payment to EasyPaisa Account: <strong>0300 1234567 (Hamim)</strong></p>}
                      {onlineMethod === 'bank' && <p>Please wire to: <strong>Meezan Bank, Acc: 0123456789 (Hamim Enterprises)</strong></p>}
                      {onlineMethod === 'paypal' && <p>Send PayPal to: <strong>payments@hamim.com</strong></p>}
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                        Upload Payment Screenshot <span className="text-red-500">*</span>
                      </label>
                      <input 
                        type="file" 
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (!file.type.startsWith('image/')) {
                              toast.error('Please upload an image file (PNG, JPG, WebP)');
                              return;
                            }
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('Screenshot image size must be under 5MB');
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = (ev) => setPaymentScreenshot(ev.target?.result as string);
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100 transition-all"
                      />
                      {paymentScreenshot && (
                        <div className="mt-2 text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                          <CheckCircle2 size={12} /> Screenshot Attached Successfully
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {paymentMethod === 'cod' && (
                  <div className="p-4 bg-orange-50 rounded-2xl border border-orange-100 space-y-4 animate-fadeIn">
                    <div className="flex gap-3 text-orange-800">
                      <ShieldAlert size={20} className="shrink-0" />
                      <div className="space-y-1 text-xs">
                        {destinationRegion === 'PK' ? (
                          <>
                            <p className="font-bold">Select Pakistan City Area for COD Delivery Fee:</p>
                            <select 
                              value={pakistanCityArea}
                              onChange={(e) => setPakistanCityArea(e.target.value as any)}
                              className="w-full mt-2 p-2 rounded-xl border border-orange-200 bg-white outline-none focus:border-orange-400 font-medium"
                            >
                              <option value="sialkot">Sialkot Local (Delivery: $2)</option>
                              <option value="nearby">Nearby Cities (Lahore, Gujranwala, Gujrat - Delivery: $6)</option>
                              <option value="other">Other Pakistan Cities (Delivery: $10)</option>
                            </select>
                            <p className="pt-2 text-[10px]">Note: For Pakistan COD, standard shipping is replaced by this area-based flat delivery fee.</p>
                          </>
                        ) : (
                          <>
                            <p className="font-bold">International Cash on Delivery</p>
                            <p>A 5% surcharge ({codSurchargeAll.usd}) applies to all international COD orders.</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button 
                type="submit"
                disabled={loading}
                className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-950 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 disabled:opacity-50 active:scale-[0.99]"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Transmitting Order to Sialkot Logistics...
                  </span>
                ) : (
                  <>
                    Confirm & Place Surgical Order
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* STEP 3: ORDER CONFIRMED & INVOICE VAULT */}
          {step === 3 && (
            <div className="py-4 text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-3xl flex items-center justify-center mx-auto border border-emerald-100 shadow-inner">
                <CheckCircle2 size={44} />
              </div>

              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-900 text-white rounded-full text-xs font-mono font-bold tracking-widest shadow-md">
                  <span>REF: #{placedOrder?.orderId}</span>
                  <button 
                    onClick={handleCopyOrderId}
                    className="p-1 hover:text-indigo-300 transition-colors"
                    title="Copy Order ID"
                  >
                    {copiedId ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  </button>
                </div>

                <h3 className="text-2xl sm:text-3xl font-black text-slate-900 uppercase tracking-tight">
                  Order Successfully Placed!
                </h3>
                <p className="text-slate-500 text-xs sm:text-sm max-w-md mx-auto">
                  Your surgical instrumentation order has been registered in the Hamim Enterprises export database and dispatched to production.
                </p>
              </div>

              {/* Order Breakdown Box */}
              <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 text-left space-y-3 text-xs">
                <div className="flex justify-between items-center pb-2 border-b border-slate-200/60 font-black uppercase text-slate-400 text-[10px]">
                  <span>Order Summary</span>
                  <span>{placedOrder?.items?.length || 0} Instruments</span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-slate-600 font-medium">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Client Name:</span>
                    <strong className="text-slate-900">{placedOrder?.customerName}</strong>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Contact / WhatsApp:</span>
                    <strong className="text-slate-900">{placedOrder?.customerPhone}</strong>
                  </div>
                  <div className="col-span-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Hospital Address:</span>
                    <strong className="text-slate-900">{placedOrder?.customerAddress}</strong>
                  </div>
                </div>

                <div className="p-3 bg-white rounded-2xl border border-slate-200 grid grid-cols-4 gap-2 text-center pt-2">
                  <div>
                    <p className="text-[9px] font-black text-slate-400">USD</p>
                    <p className="text-xs font-black text-slate-900">{placedOrder?.multiCurrency?.usd}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-indigo-500">PKR</p>
                    <p className="text-xs font-black text-indigo-700">{placedOrder?.multiCurrency?.pkr}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-amber-500">CNY</p>
                    <p className="text-xs font-black text-amber-700">{placedOrder?.multiCurrency?.cny}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-purple-500">EUR</p>
                    <p className="text-xs font-black text-purple-700">{placedOrder?.multiCurrency?.eur}</p>
                  </div>
                </div>
              </div>

              {/* Official Invoice & Quotation Download Action Grid */}
              <div className="p-5 bg-white rounded-3xl border border-slate-200 shadow-sm space-y-3 text-left">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                    <FileText size={14} className="text-indigo-600" /> Official Export Documentation
                  </span>
                  <span className="text-[10px] font-bold text-emerald-600">Dual Export Formats</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => handleDownloadInvoice('pdf', 'Invoice')}
                    className="py-3.5 px-3 bg-slate-900 text-white hover:bg-indigo-600 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-sm"
                  >
                    <Download size={14} className="text-red-400" />
                    Download Invoice (PDF)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownloadInvoice('png', 'Invoice')}
                    className="py-3.5 px-3 bg-slate-900 text-white hover:bg-indigo-600 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-sm"
                  >
                    <Download size={14} className="text-cyan-400" />
                    Download Invoice (PNG)
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2.5 pt-1">
                  <button
                    type="button"
                    onClick={() => handleDownloadInvoice('pdf', 'Quotation')}
                    className="py-3 px-3 bg-slate-100 hover:bg-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-700 transition-all flex items-center justify-center gap-1.5"
                  >
                    <FileText size={13} className="text-amber-600" />
                    Download Quote (PDF)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownloadInvoice('png', 'Quotation')}
                    className="py-3 px-3 bg-slate-100 hover:bg-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-700 transition-all flex items-center justify-center gap-1.5"
                  >
                    <FileText size={13} className="text-indigo-600" />
                    Download Quote (PNG)
                  </button>
                </div>
              </div>

              {/* Direct WhatsApp Order Confirmation Dispatch */}
              <button
                type="button"
                onClick={handleWhatsAppDispatch}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-100"
              >
                <MessageCircle size={18} />
                Send Order & Receipt to WhatsApp Admin (+92 323 5857576)
              </button>

              <button 
                onClick={onClose}
                className="w-full py-4 bg-slate-100 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all"
              >
                Return to Surgical Catalog
              </button>
            </div>
          )}

        </div>
      </motion.div>
    </div>
  );
};
